// Phase 15 Plan 08 Task 3 — Per-ladder mode switcher.
// Phase 17 Plan 03 Task 2 — Migrated off the prior pills picker to the new
// Seg primitive (big-bang rename per CONTEXT.md Area 2 Q2). External
// LadderSwitcher API unchanged.
//
// Wraps the Phase 17 Seg primitive (radiogroup) with the 3 ranked ladder MODES
// per DEC-010:
//   - standard_4 (Standard 4L)
//   - open_3     (Open 3L)
//   - purist
//
// Locale is implicit from $locale — the full LadderKey is built as
// `${mode}_${locale}` (e.g. 'standard_4_en'). The 2 casual pools (DEC-010)
// are intentionally NOT exposed on the leaderboard switcher; only the 6
// ranked ladders surface here, split en/sv by the active locale.
//
// 15-UI-SPEC.md §"Ladder switcher" — Sun-Yellow selected pill body + Inkwell
// label; unselected pills are surface-mid. The .seg recipe in pieces.css
// (Phase 17 Plan 01) delivers this verbatim.

import type { JSX } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import type { LadderKey } from '../../net/leaderboard'
import type { Locale } from '../../stores/locale'
import { Seg, type SegOption } from '../components/Seg'

export type LadderMode = 'standard_4' | 'open_3' | 'purist'

const MODE_OPTIONS: ReadonlyArray<SegOption<LadderMode>> = [
  {
    value: 'standard_4',
    label: (
      <FormattedMessage
        id="social.leaderboard.ladder.standard_4l"
        defaultMessage="Standard 4L"
      />
    ),
  },
  {
    value: 'open_3',
    label: (
      <FormattedMessage
        id="social.leaderboard.ladder.open_3l"
        defaultMessage="Open 3L"
      />
    ),
  },
  {
    value: 'purist',
    label: (
      <FormattedMessage
        id="social.leaderboard.ladder.purist"
        defaultMessage="Purist"
      />
    ),
  },
]

/**
 * Build the full LadderKey from a mode + locale. The schema enumerates 6
 * keys: standard_4_en/sv, open_3_en/sv, purist_en/sv.
 */
export function buildLadderKey(mode: LadderMode, locale: Locale): LadderKey {
  return `${mode}_${locale}` as LadderKey
}

export interface LadderSwitcherProps {
  readonly selectedMode: LadderMode
  readonly onSelect: (mode: LadderMode) => void
}

export function LadderSwitcher(props: LadderSwitcherProps): JSX.Element {
  const { selectedMode, onSelect } = props
  const intl = useIntl()
  // Locale only affects the constructed LadderKey via buildLadderKey()
  // (called by the LeaderboardScreen caller). The visible copy is rendered
  // by Seg via the inline <FormattedMessage> in each option, which picks up
  // the IntlProvider's current locale automatically.

  return (
    <Seg<LadderMode>
      options={MODE_OPTIONS}
      value={selectedMode}
      onChange={onSelect}
      ariaLabel={intl.formatMessage({
        id: 'social.leaderboard.ladder_switcher_aria',
      })}
      testId="ladder-switcher"
    />
  )
}
